import type {
  RichTextDocument,
  RichTextMark,
  RichTextNode,
} from "@/types/models";

const BLOCK_BREAKS = new Set([
  "paragraph",
  "heading",
  "listItem",
  "tableRow",
  "secureImage",
]);

export function emptyRichTextDocument(): RichTextDocument {
  return {
    version: 1,
    doc: { type: "doc", content: [{ type: "paragraph" }] },
  };
}

export function richTextFromPlainText(value = ""): RichTextDocument {
  const lines = value.replace(/\r\n?/g, "\n").split("\n");
  return {
    version: 1,
    doc: {
      type: "doc",
      content: lines.map((line) => ({
        type: "paragraph",
        ...(line ? { content: [{ type: "text", text: line }] } : {}),
      })),
    },
  };
}

type LegacyProjectBriefBlock = {
  type?: unknown;
  text?: unknown;
  href?: unknown;
};

type LegacyProjectBriefSection = {
  title?: unknown;
  blocks?: unknown;
};

function textNode(
  text: string,
  marks?: RichTextMark[],
): RichTextNode | undefined {
  return text
    ? { type: "text", text, ...(marks?.length ? { marks } : {}) }
    : undefined;
}

/** Converts the retired section/block brief shape during rolling upgrades. */
export function richTextFromLegacyProjectBrief(
  value: unknown,
): RichTextDocument {
  if (!Array.isArray(value) || !value.length) return emptyRichTextDocument();
  const content: RichTextNode[] = [];
  for (const rawSection of value as LegacyProjectBriefSection[]) {
    const title = String(rawSection?.title || "").trim();
    if (title) {
      const headingText = textNode(title);
      content.push({
        type: "heading",
        attrs: { level: 2 },
        ...(headingText ? { content: [headingText] } : {}),
      });
    }
    const blocks = Array.isArray(rawSection?.blocks)
      ? (rawSection.blocks as LegacyProjectBriefBlock[])
      : [];
    let bulletItems: RichTextNode[] = [];
    const flushBullets = () => {
      if (!bulletItems.length) return;
      content.push({ type: "bulletList", content: bulletItems });
      bulletItems = [];
    };
    for (const block of blocks) {
      const blockText = String(block?.text || "");
      if (block?.type === "bullet") {
        const bulletText = textNode(blockText);
        bulletItems.push({
          type: "listItem",
          content: [
            {
              type: "paragraph",
              ...(bulletText ? { content: [bulletText] } : {}),
            },
          ],
        });
        continue;
      }
      flushBullets();
      const marks: RichTextMark[] = [];
      if (block?.type === "emphasis") marks.push({ type: "bold" });
      if (block?.type === "link" && typeof block.href === "string") {
        const href = normalizeSafeUrl(block.href);
        if (href) marks.push({ type: "link", attrs: { href } });
      }
      const paragraphText = textNode(blockText, marks);
      content.push({
        type: "paragraph",
        ...(paragraphText ? { content: [paragraphText] } : {}),
      });
    }
    flushBullets();
  }
  return sanitizeRichTextForPersistence({
    version: 1,
    doc: {
      type: "doc",
      content: content.length ? content : [{ type: "paragraph" }],
    },
  });
}

export function richTextOrPlain(
  document: RichTextDocument | undefined,
  plainText: string | undefined,
): RichTextDocument {
  return document?.version === 1
    ? sanitizeRichTextForPersistence(document)
    : richTextFromPlainText(plainText || "");
}

export function richTextPlainText(document: RichTextDocument): string {
  let output = "";
  const visit = (node: RichTextNode) => {
    if (node.type === "text") output += node.text || "";
    else if (node.type === "mention")
      output += `@${node.attrs?.label || "Project participant"}`;
    else if (node.type === "hardBreak") output += "\n";
    else if (node.type === "secureImage")
      output += node.attrs?.alt ? `[Image: ${node.attrs.alt}]` : "[Image]";
    node.content?.forEach(visit);
    if (BLOCK_BREAKS.has(node.type) && output && !output.endsWith("\n"))
      output += "\n";
  };
  visit(document.doc);
  return output
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function richTextIsEmpty(document: RichTextDocument): boolean {
  return !richTextPlainText(document);
}

export function richTextMentionIds(document: RichTextDocument): string[] {
  const ids = new Set<string>();
  const visit = (node: RichTextNode) => {
    if (node.type === "mention" && node.attrs?.id) ids.add(node.attrs.id);
    node.content?.forEach(visit);
  };
  visit(document.doc);
  return [...ids];
}

export function replaceIneligibleMentions(
  document: RichTextDocument,
  allowedParticipantIds: Iterable<string>,
): RichTextDocument {
  const allowed = new Set(allowedParticipantIds);
  const replace = (node: RichTextNode): RichTextNode => {
    if (node.type === "mention" && !allowed.has(String(node.attrs?.id || "")))
      return {
        type: "text",
        text: `@${node.attrs?.label || "Project participant"}`,
      };
    return {
      ...node,
      ...(node.content ? { content: node.content.map(replace) } : {}),
    };
  };
  return sanitizeRichTextForPersistence({
    version: 1,
    doc: replace(document.doc),
  });
}

function cleanMark(mark: RichTextMark): RichTextMark | null {
  if (["bold", "italic", "underline"].includes(mark.type))
    return { type: mark.type };
  if (mark.type !== "link" || !mark.attrs?.href) return null;
  const href = normalizeSafeUrl(mark.attrs.href);
  return href ? { type: "link", attrs: { href } } : null;
}

function cleanNode(node: RichTextNode): RichTextNode {
  const output: RichTextNode = { type: node.type };
  if (node.type === "text") {
    output.text = String(node.text || "");
    const marks = (node.marks || []).flatMap((mark) => {
      const cleaned = cleanMark(mark);
      return cleaned ? [cleaned] : [];
    });
    if (marks.length) output.marks = marks;
  }
  if (node.type === "heading")
    output.attrs = { level: node.attrs?.level === 3 ? 3 : 2 };
  if (node.type === "orderedList" && Number(node.attrs?.start) > 1)
    output.attrs = { start: Math.max(1, Number(node.attrs?.start)) };
  if (["tableCell", "tableHeader"].includes(node.type)) {
    output.attrs = {
      colspan: Math.max(1, Number(node.attrs?.colspan || 1)),
      rowspan: Math.max(1, Number(node.attrs?.rowspan || 1)),
      colwidth: Array.isArray(node.attrs?.colwidth)
        ? node.attrs.colwidth.map(Number).filter(Number.isFinite).slice(0, 20)
        : null,
    };
  }
  if (node.type === "mention") {
    output.attrs = {
      id: String(node.attrs?.id || "").slice(0, 128),
      label: String(node.attrs?.label || "Project participant").slice(0, 160),
    };
  }
  if (node.type === "secureImage") {
    output.attrs = {
      fileId: String(node.attrs?.fileId || "").slice(0, 100),
      alt: String(node.attrs?.alt || "Pasted image").slice(0, 500),
      state: ["available", "blocked"].includes(String(node.attrs?.state))
        ? (node.attrs?.state as "available" | "blocked")
        : "checking",
    };
  }
  if (node.content?.length)
    output.content = node.content.map(cleanNode).slice(0, 1_000);
  return output;
}

export function sanitizeRichTextForPersistence(
  document: RichTextDocument,
): RichTextDocument {
  const doc = cleanNode(document.doc);
  return {
    version: 1,
    doc:
      doc.type === "doc"
        ? doc
        : { type: "doc", content: [{ type: "paragraph" }] },
  };
}

export function normalizeSafeUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const candidate = /^[a-z][a-z\d+.-]*:/i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    return ["http:", "https:", "mailto:"].includes(url.protocol)
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

export function cleanWordHtml(html: string): string {
  if (!html || typeof DOMParser === "undefined") return html;
  const document = new DOMParser().parseFromString(html, "text/html");
  document
    .querySelectorAll("script, style, meta, link, xml, iframe, object, embed")
    .forEach((node) => node.remove());

  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_COMMENT,
  );
  const comments: Node[] = [];
  while (walker.nextNode()) comments.push(walker.currentNode);
  comments.forEach((node) => node.parentNode?.removeChild(node));

  document.body.querySelectorAll<HTMLElement>("*").forEach((element) => {
    const style = element.style;
    if (/^(bold|[6-9]00)$/i.test(style.fontWeight)) {
      const strong = document.createElement("strong");
      while (element.firstChild) strong.append(element.firstChild);
      element.append(strong);
    }
    if (style.fontStyle === "italic") {
      const emphasis = document.createElement("em");
      while (element.firstChild) emphasis.append(element.firstChild);
      element.append(emphasis);
    }
    if (style.textDecorationLine.includes("underline")) {
      const underline = document.createElement("u");
      while (element.firstChild) underline.append(element.firstChild);
      element.append(underline);
    }
    for (const attribute of [...element.attributes]) {
      if (
        !["href", "colspan", "rowspan"].includes(
          attribute.name.toLocaleLowerCase(),
        )
      )
        element.removeAttribute(attribute.name);
    }
    if (element instanceof HTMLAnchorElement) {
      const href = normalizeSafeUrl(element.getAttribute("href") || "");
      if (href) element.setAttribute("href", href);
      else element.removeAttribute("href");
    }
  });
  return document.body.innerHTML;
}
